// Core
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// Styles
import "./styles.scss";

// Assets
import userIcon from "./../../../Assets/Icons/userIcon.png";
import passwordIcon from "./../../../Assets/Icons/passwordIcon.png";
import emailIcon from "./../../../Assets/Icons/emailIcon.png";
import closeIcon from "./../../../Assets/Icons/closeIcon.png";

// Redux
import { useDispatch } from "react-redux";
import {
  loginRequest,
  triggerEmailForForgotPasswordRequest,
} from "../../../Redux/Slices/authSlice";

// Custom Layouts
import AuthLayout from "Layouts/Auth";

// Utils
import { validateInput } from "Services/Utils/validateInput";
import { getDeviceId } from "Services/Utils/commonServices";

// Custom components
import InputBox from "Components/InputBox";
import Button from "Components/Button";
import ErrorMessage from "Components/ErrorMessage";
import { notifySuccess } from "Services/Utils/toastNotification";

const Login = () => {
  // Initialize useNavigate
  const navigate = useNavigate();

  // Initialize redux
  const dispatch = useDispatch();

  const [isForgotPasswordVisible, setIsForgotPasswordVisible] = useState(false);
  const [isLoginVisible, setIsLoginVisible] = useState(true);

  const toggleForgotPassword = () => {
    setIsForgotPasswordVisible(!isForgotPasswordVisible);
  };

  // Local states for Login form
  const initialFormData = { 
    email: localStorage.getItem('rememberedEmail') || "", 
    password: "" ,
    otp: ""
  };
  const initialErrors = {};

  // Local states for Forgot Password form
  const initialFormDataForgotPassword = { email: "" };
  const initialErrorsForgotPassword = {};

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);

  const [formDataForgotPassword, setFormDataForgotPassword] = useState(
    initialFormDataForgotPassword
  );
  const [errorsForgotPassword, setErrorsForgotPassword] = useState(
    initialErrorsForgotPassword
  );

  const [rememberMe, setRememberMe] = useState(() => {
    // Check if there's a stored email
    const storedEmail = localStorage.getItem('rememberedEmail');
    return !!storedEmail;
  });

  // Handle changes for Login form inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle changes for Forgot Password form inputs
  const handleForgotPasswordChange = (e) => {
    const { name, value } = e.target;
    setFormDataForgotPassword((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Reset form data and errors for Login
  const resetForm = () => {
    setFormData(initialFormData);
    setErrors(initialErrors);
    setFormDataForgotPassword(initialFormDataForgotPassword);
    setErrorsForgotPassword(initialErrorsForgotPassword);
  };

  // Validate form data for Login
  const handleValidation = () => {
    let validationErrors = {};
  
    Object.keys(formData).forEach((key) => {
      const error = validateInput(key, formData[key]);
      if (Object.keys(error).length > 0) {
        validationErrors = { ...validationErrors, ...error };
      }
    });
  
    if (!isLoginVisible) {  
      if (!formData.otp || formData.otp.trim() === "") {
        validationErrors.otp = "OTP is required";
      } else if (!/^\d{6}$/.test(formData.otp)) { 
        validationErrors.otp = "OTP must be a 6-digit number";
      }
    }
  
    setErrors(validationErrors);
    return validationErrors;
  };

  // Validate form data for Forgot Password
  const handleForgotPasswordValidation = () => {
    let validationErrorsForgotPassword = {};
    const error = validateInput("email", formDataForgotPassword.email);
    if (Object.keys(error).length > 0) {
      validationErrorsForgotPassword = {
        ...validationErrorsForgotPassword,
        ...error,
      };
    }
    setErrorsForgotPassword(validationErrorsForgotPassword);
    return validationErrorsForgotPassword;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = handleValidation();
  
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
  
    let requestData = {
      identifier: formData.email,
      password: formData.password,
      device_token: getDeviceId(),
    };
  
    dispatch(
      loginRequest({
        data: requestData,
        callBack: loginRequestCB,
      })
    );
  };
  
  const loginRequestCB = (data) => {
    if (data?.message === "Otp sent successfully!" && data?.status === 200) {
      setIsLoginVisible(false); 
      notifySuccess("OTP sent successfully! Please enter the OTP to continue.");
      setFormData(prevData => ({
        ...prevData,
        otp: '' 
      }));
      return;
    }
  
    if (data?.data?.access_details) {
      notifySuccess("Login successful!");
      localStorage.setItem("isLoggedIn", true);
  
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", formData.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
  
      if (data?.data?.access_details.includes("Dashboard") || data?.data?.role === 1) {
        navigate("/");
      } else {
        const firstAccessDetail = data?.data?.access_details[0];
        const specialRoutes = {
          "Request Management": "/request",
          "User Management": "/userManagement",
        };
        const formattedPath = specialRoutes[firstAccessDetail] || `/${firstAccessDetail.replace(/\s+/g, '')}`;
        navigate(formattedPath);
      }
    }
  };
  
  const handleOTPSubmit = (e) => {
  e.preventDefault();
  localStorage.removeItem("userDetails");
  
  const validationErrors = handleValidation();
  if (Object.keys(validationErrors).length > 0) {
    return;  // Stop submission if validation fails
  }

  const requestData = {
    identifier: formData.email,
    password: formData.password,
    otp: formData.otp,
    device_token: getDeviceId(),
  };

  dispatch(
    loginRequest({
      data: requestData,
      callBack: loginRequestCB,
    })
  );
};


  // Handle form submission for Forgot Password
  const handleForgotPasswordSubmit = (e) => {
    e.preventDefault();
    const validationErrors = handleForgotPasswordValidation();
    console.log(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      // Proceed with Forgot Password submission
      dispatch(
        triggerEmailForForgotPasswordRequest({
          email: formDataForgotPassword?.email,
          callBack: triggerEmailForForgotPasswordRequestCB,
        })
      );
    }
  };

  const triggerEmailForForgotPasswordRequestCB = () => {
    notifySuccess(
      "A password reset email has been sent to your registered email address."
    );
    toggleForgotPassword();
    resetForm();
  };

  const handleRememberMe = (e) => {
    setRememberMe(e.target.checked);
  };

  const handleBackToLogin = () => {
    setIsLoginVisible(true);
    localStorage.removeItem("userDetails");
    setFormData(prevData => ({
      ...prevData,
      otp: '' 
    }));
    setErrors({}); 
  };

  return (
    <AuthLayout>
      <form
         onSubmit={!isLoginVisible ? handleOTPSubmit : handleSubmit}
        className={`login-root-container ${
          isForgotPasswordVisible ? "overlay-active" : ""
        }`}
      >
        <h1 className="login-heading">
        {isLoginVisible ? "Sign in to KG Ultra" : "Enter OTP to Verify"}
        </h1>
        {isLoginVisible ? (
  <>
        <div className="inputBoxes">
          <div className="inputBox">
            <InputBox
              icon={userIcon}
              placeholder="Email ID"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
            <ErrorMessage message={errors.email} />
          </div>
          <div className="inputBox">
            <InputBox
              icon={passwordIcon}
              placeholder="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              isPassword={true}
            />
            <ErrorMessage message={errors.password} />
          </div>
        </div>
        <div className="subSelections">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              fontSize: "13px",
            }}
          >
            <input 
              type="checkbox" 
              checked={rememberMe}
              onChange={handleRememberMe}
            />
            Remember me
          </div>
          <div
            style={{ fontSize: "13px", fontWeight: "700", cursor: "pointer" }}
            onClick={toggleForgotPassword}
          >
            Forgot Password?
          </div>
        </div>
        <div className="buttonDiv">
          <Button forForm={true} title="Login" />
        </div>
        </>
        ) : 
        (
          <>
          <div className="inputBoxes">
            <div className="inputBox">
                  <InputBox
                    icon={passwordIcon}
                    placeholder="Enter the OTP"
                    name="otp"  // Change name to "otp"
                    value={formData.otp}
                    onChange={handleChange}
                    maxLength={6}
                      // Uncomment this
                  />
              <ErrorMessage message={errors.otp} />
            </div>
            <div
              style={{ fontSize: "13px", fontWeight: "700", cursor: "pointer" }}
              onClick={()=>{setIsLoginVisible(!isLoginVisible)
                handleBackToLogin()
              }}
            >
              Back to Login
            </div>
          </div>
            <div className="buttonDiv">
              <Button forForm={true} type="submit" title="Verify OTP" />
            </div>
        </>
      )}
      </form>

      <div className="copyrightContainer">
        <div>
        Copyright 2024 KG Ultra
        </div>
        <div className='policyContainer'>
          <p style={{cursor:"pointer"}} onClick={()=>navigate('/privacy-policy', { state: { key: 1 }})}>Privacy Policy</p>
          <p style={{cursor:"pointer"}} onClick={()=>navigate('/terms-of-use', { state: { key: 2 }})}>Term of use</p>
        </div>
      </div>

      {isForgotPasswordVisible && (
        <div className="forgotPasswordContainer">
          <img
            className="closeIcon"
            src={closeIcon}
            alt="closeIcon"
            onClick={toggleForgotPassword}
          />
          <form onSubmit={handleForgotPasswordSubmit} className="content">
            <h2 className="">Registered Email Address</h2>
            <div className="inputBox">
              <InputBox
                icon={emailIcon}
                placeholder="Email Address"
                name="email"
                value={formDataForgotPassword.email}
                onChange={handleForgotPasswordChange}
              />
            <ErrorMessage message={errorsForgotPassword.email} />
            </div>
            <div className="buttonDiv">
              <Button forForm={true} title="Submit" />
            </div>
          </form>
        </div>
      )}
    </AuthLayout>
  );
};

export default Login;
